import {
    DIRECT,
    NEW_SMART_FORM_ITEM,
    SERVICE,
    STOCK,
    TYPE_OPTIONS,
} from "constants/generalConstants";
import {
    setActiveSmartForm,
    setShowSmartFormsDialogue,
} from "redux/actions/generalActions";
import {
    setProduct,
    setSmartFormsDataLoading,
} from "redux/actions/nonCatFormActions";
import store from "redux/store";
import { smartFormItemBase } from "schemas/nonCatalogueCartItemBaseSchema";
import { getCpaById } from "services/search/searchAPIs";
import { v4 as uuidv4 } from "uuid";

import {
    nonCatProductServiceSettings,
    openNonCatDialogue,
} from "./nonCatFormUtils";

const RESTRICT_SUPPLIER_TYPE_ASL_VALUE = "RESTRICT_CATEGORY_ASL";
const TOTAL_AMOUNT_KW = "TOTAL_AMOUNT";
export const SMART_FORM_NON_CAT_ITEM_TYPE_DISPATCHER = {
    GOODS: TYPE_OPTIONS[0],
    SERVICES: TYPE_OPTIONS[1],
    [TOTAL_AMOUNT_KW]: TYPE_OPTIONS[2],
    [TYPE_OPTIONS[2]]: TYPE_OPTIONS[2],
    [TYPE_OPTIONS[1]]: TYPE_OPTIONS[1],
    [TYPE_OPTIONS[0]]: TYPE_OPTIONS[0],
    DEFAULT: TYPE_OPTIONS[0],
};

export const SMART_FORM_LINE_TYPE_DISPATCHER = {
    [TYPE_OPTIONS[2]]: SERVICE,
    [TYPE_OPTIONS[1]]: DIRECT,
    [TYPE_OPTIONS[0]]: STOCK,
};

// Use designated opener function
export const openSmartFormModal = (smartForm, dispatch, browsingOU) => {
    // TODO - async get Smart Form by id
    dispatch(setSmartFormsDataLoading(true));
    openNonCatDialogue(
        dispatch,
        NEW_SMART_FORM_ITEM,
        null,
        null,
        null,
        browsingOU,
        smartForm
    );
};

/**
 *
 * @param {*} smartForm
 * @returns
 */
export const setUseApprovedSupplierList = (smartForm) => {
    const isAsl =
        smartForm?.RESTRICT_SUPPLIER_TYPE === RESTRICT_SUPPLIER_TYPE_ASL_VALUE;
    return { ...smartForm, USE_APPROVED_SUPPLIERS: isAsl };
};

/**
 * Loads blank Smart Form item into redux
 * @param {Function} dispatch - redux dispatcher
 * @param {string} activeOU - browsing OU in which item is being created
 *
 * Note - `PO_LINE_ID` is a unique ID for a non-catalogue item
 */
export const newSmartFormItem = async (
    dispatch,
    activeOU,
    smartForm,
    isEditMode = false
) => {
    let augmentedSmartForm = smartForm;
    if (smartForm?.CONTRACT_ID) {
        augmentedSmartForm = await getSmartFormSupplementingInformation(
            smartForm,
            activeOU,
            dispatch
        );
    }

    if (!isEditMode && augmentedSmartForm?.INFORMATION_TEMPLATE_COLLECTION) {
        augmentedSmartForm["INFORMATION_TEMPLATES"] =
            augmentedSmartForm.INFORMATION_TEMPLATE_COLLECTION;
    }

    dispatch(
        setActiveSmartForm(setUseApprovedSupplierList(augmentedSmartForm))
    );
    dispatch(
        setProduct({
            ...smartFormItemBase,
            PO_LINE_ID: augmentedSmartForm?.PO_LINE_ID
                ? augmentedSmartForm?.PO_LINE_ID
                : uuidv4(), // Give Smart Form item a local UUID
            IBUY_CART_ITEM_OU: activeOU,
            ...parseNonCatProductFromSmartForm(augmentedSmartForm),
            CLEAR_SMART_FORM: "1",
        })
    );
    dispatch(setSmartFormsDataLoading(false));
};

/**
 * Makes async calls to retrieve data to support loading the
 * smartform page
 * @param {Object} smartForm - smartform being loaded
 * @param {string} browsingOU - OU in which smartform is being loaded
 * @param {Function} dispatch - redux dispatcher function
 */
export const getSmartFormSupplementingInformation = async (
    smartForm,
    browsingOU,
    dispatch
) => {
    // TODO - get IFT
    const cpa = getCpaById(smartForm?.CONTRACT_ID, browsingOU, dispatch);
    let augmentedSmartForm;
    await Promise.all([cpa])
        .then((values) => {
            augmentedSmartForm = { ...smartForm, ...values[0] };
        })
        .catch(() => console.log("TODO - handle error"));
    return augmentedSmartForm;
};

/**
 * Handles closing of Smart Forms dialogue. Note - this function
 * does not clear out product. Product should be reset from
 * scratch when the form is launched
 * @param {Function} dispatch - redux dispatcher function
 */
export const closeSmartFormsDialogue = (dispatch) => {
    dispatch(setShowSmartFormsDialogue(false));
};

/**
 * Parses a non-catalogue item redux state object from a Smart Form
 * @param {object} activeSmartForm - Smart Form being loaded
 * @returns {object} - object of non-catalogue redux field mapped
 * to values specified in the Smart Form being opened
 */
export const parseNonCatProductFromSmartForm = (activeSmartForm) => ({
    ITEM_TYPE:
        SMART_FORM_NON_CAT_ITEM_TYPE_DISPATCHER[activeSmartForm?.ITEM_TYPE] ||
        SMART_FORM_NON_CAT_ITEM_TYPE_DISPATCHER["DEFAULT"],
    SMART_FORM_ID: activeSmartForm?.TEMPLATE_ID
        ? activeSmartForm?.TEMPLATE_ID
        : activeSmartForm?.SMART_FORM_TEMPLATE_ID,
    ITEM_DESCRIPTION: activeSmartForm?.ITEM_DESCRIPTION
        ? activeSmartForm?.ITEM_DESCRIPTION
        : activeSmartForm?.TEMPLATE_NAME,
    TEMPLATE_NAME: activeSmartForm?.TEMPLATE_NAME
        ? activeSmartForm?.TEMPLATE_NAME
        : activeSmartForm?.ITEM_DESCRIPTION,
    title: activeSmartForm?.SUPPLIER_CONTACT_NAME,
    SUPPLIER_CONTACT_NAME: activeSmartForm?.SUPPLIER_CONTACT_NAME,
    UNIT_OF_MEASURE: activeSmartForm?.UNIT_OF_MEASURE,
    UNIT_MEAS_LOOKUP_CODE: activeSmartForm?.UNIT_MEAS_LOOKUP_CODE,
    CPA_NUMBER: activeSmartForm?.CONTRACT
        ? activeSmartForm?.CONTRACT
        : activeSmartForm?.CPA_NUMBER,
    SUPPLIER_EDITABLE_FLAG: activeSmartForm?.SUPPLIER_EDITABLE_FLAG,
    CONTRACT: activeSmartForm?.CONTRACT,
    CPA_NUM_NAME: activeSmartForm?.CPA_NUM_NAME,
    CURRENCY_CODE: activeSmartForm?.CURRENCY_CODE || activeSmartForm?.CURRENCY,
    CONTRACT_ID: activeSmartForm?.CONTRACT_ID,
    TEMPLATE_ID: activeSmartForm?.TEMPLATE_ID
        ? activeSmartForm?.TEMPLATE_ID
        : activeSmartForm?.SMART_FORM_TEMPLATE_ID,
    SMART_FORM_TEMPLATE_ID: activeSmartForm?.TEMPLATE_ID
        ? activeSmartForm?.TEMPLATE_ID
        : activeSmartForm?.SMART_FORM_TEMPLATE_ID,
    QUANTITY: activeSmartForm?.QUANTITY,
    UNIT_PRICE: activeSmartForm?.UNIT_PRICE,
    LINE_TYPE:
        activeSmartForm?.LINE_TYPE ||
        SMART_FORM_LINE_TYPE_DISPATCHER[
            SMART_FORM_NON_CAT_ITEM_TYPE_DISPATCHER[activeSmartForm?.ITEM_TYPE]
        ] ||
        "",
    DESC_EDITABLE_FLAG: activeSmartForm?.DESC_EDITABLE_FLAG,
    CAT_EDITABLE_FLAG: activeSmartForm?.CAT_EDITABLE_FLAG,
    UOM_EDITABLE_FLAG: activeSmartForm?.UOM_EDITABLE_FLAG,
    PRICE_EDITABLE_FLAG: activeSmartForm?.PRICE_EDITABLE_FLAG,
    CURRENCY: activeSmartForm?.CURRENCY,
    CURRENCY_EDITABLE_FLAG: activeSmartForm?.CURRENCY_EDITABLE_FLAG,
    RATE_EDITABLE_FLAG: activeSmartForm?.RATE_EDITABLE_FLAG,
    RATE_TYPE_EDITABLE_FLAG: activeSmartForm?.RATE_TYPE_EDITABLE_FLAG,
    RATE_DATE_EDITABLE_FLAG: activeSmartForm?.RATE_DATE_EDITABLE_FLAG,
    SITE_EDITABLE_FLAG: activeSmartForm?.SITE_EDITABLE_FLAG,
    CONTACT_EDITABLE_FLAG: activeSmartForm?.CONTACT_EDITABLE_FLAG,
    PHONE_EDITABLE_FLAG: activeSmartForm?.PHONE_EDITABLE_FLAG,
    SUPPLIER_CONTACT_FAX: activeSmartForm?.SUPPLIER_CONTACT_FAX,
    FAX_EDITABLE_FLAG: activeSmartForm?.FAX_EDITABLE_FLAG,
    SUPPLIER_CONTACT_EMAIL: activeSmartForm?.SUPPLIER_CONTACT_EMAIL,
    EMAIL_EDITABLE_FLAG: activeSmartForm?.EMAIL_EDITABLE_FLAG,
    SUPPLIER_ITEM: activeSmartForm?.SUPPLIER_ITEM,
    SUPPLIER_ITEM_EDITABLE_FLAG: activeSmartForm?.SUPPLIER_ITEM_EDITABLE_FLAG,
    MANUFACTURER: activeSmartForm?.MANUFACTURER,
    MANUFACTURER_EDITABLE_FLAG: activeSmartForm?.MANUFACTURER_EDITABLE_FLAG,
    MANUFACTURER_PART_NUMBER: activeSmartForm?.MANUFACTURER_PART_NUMBER,
    MPN_EDITABLE_FLAG: activeSmartForm?.MPN_EDITABLE_FLAG,
    NEGOTIATED_BY_PREPARER_FLAG: activeSmartForm?.NEGOTIATED_BY_PREPARER_FLAG,
    NEGOTIATED_EDITABLE_FLAG: activeSmartForm?.NEGOTIATED_EDITABLE_FLAG,
    RFQ_REQUIRED_FLAG: activeSmartForm?.RFQ_REQUIRED_FLAG,
    RFQ_REQ_EDITABLE_FLAG: activeSmartForm?.RFQ_REQ_EDITABLE_FLAG,
    CONTRACT_EDITABLE_FLAG: activeSmartForm?.CONTRACT_EDITABLE_FLAG,
    RATE: activeSmartForm?.RATE,
    RATE_DATE: activeSmartForm?.RATE_DATE,
    RATE_TYPE: activeSmartForm?.RATE_TYPE,
    INFORMATION_TEMPLATES: activeSmartForm?.INFORMATION_TEMPLATES,
    SMARTFORM_TEMPLATE_ID: activeSmartForm?.SMARTFORM_TEMPLATE_ID,
    fromCart: activeSmartForm?.fromCart,
    ...parseSmartFormUOMFromLookups(activeSmartForm),
    ...parseSmartFormCategoryFromLookups(activeSmartForm),
    ...parseFromSmartFormSupplierInfo(activeSmartForm),
    ...(activeSmartForm?.ITEM_TYPE === TOTAL_AMOUNT_KW
        ? nonCatProductServiceSettings
        : {}),
});

/**
 * Parses supplier (non-cpa) information when a Smart Form is being
 * loaded into the iBuy non-catalogue form. If the Smart Form is a
 * CPA Smart Form, an empty object is returned; in this case, supplier
 * related fields should be null, handled by `smartFormItemBase`. If
 * it is a non-CPA Smart Form, then an object with all supplier
 * fields required to be loaded into redux is returned (overwriting
 * empty values in `smartFormItemBase`)
 * @param {*} activeSmartForm - Smart Form being loaded
 * @returns {object} supplier information to be injected into Smart Form
 */
export const parseFromSmartFormSupplierInfo = (activeSmartForm) => {
    // const isCpaSmartForm = activeSmartForm?.CONTRACT_ID;
    return {
        SUPPLIER_ID: activeSmartForm?.SUPPLIER_ID,
        SUPPLIER_NAME: activeSmartForm?.SUPPLIER_NAME,
        SUPPLIER_NUMBER_NAME: `${activeSmartForm?.SUPPLIER_ID}-${activeSmartForm?.SUPPLIER_NAME}`,
        SUPPLIER_SITE_ID: activeSmartForm?.SUPPLIER_SITE_ID,
        SUPPLIER_SITE_CODE: activeSmartForm?.SUPPLIER_SITE_CODE,
        SUGGESTED_VENDOR_PHONE: activeSmartForm?.SUPPLIER_CONTACT_PHONE,
        SUPPLIER_CONTACT_PHONE: activeSmartForm?.SUPPLIER_CONTACT_PHONE,
        SUGGESTED_VENDOR_CONTACT_ID: activeSmartForm?.SUPPLIER_CONTACT_ID,
        SUPPLIER_CONTACT_ID: activeSmartForm?.SUPPLIER_CONTACT_ID,
        SUPPLIER_CONTACT_NAME: activeSmartForm?.SUPPLIER_CONTACT_NAME,
        SUPPLIER_CONTACT_FAX: activeSmartForm?.SUPPLIER_CONTACT_FAX,
        SUPPLIER_CONTACT_EMAIL: activeSmartForm?.SUPPLIER_CONTACT_EMAIL,
        SUPPLIER_ITEM: activeSmartForm?.SUPPLIER_ITEM,
    };
};

/**
 * Extracts category from Smart Form and parses into redux non-catalogue
 * format using categories in lookups redux
 * @param {object} activeSmartForm - Smart Form being loaded
 * lookups store
 * @returns {Object} - category values to be injected into non-catalogue form
 */
export const parseSmartFormCategoryFromLookups = (activeSmartForm) => {
    const reduxLookupsCategories =
        store.getState().filterPanelLookups.CATEGORY_DISPLAY || [];
    const selectedCategory = reduxLookupsCategories.filter((category) => {
        return (
            activeSmartForm?.PURCHASING_CATEGORY &&
            activeSmartForm?.PURCHASING_CATEGORY == category?.CATEGORY
        );
    });

    const defaultCategoryValues = {
        CATEGORY_ID: activeSmartForm?.CATEGORY_ID,
        CATEGORY: activeSmartForm?.CATEGORY,
        CATEGORY_DISPLAY_NAME: activeSmartForm?.CATEGORY_DISPLAY_NAME,
        PURCHASING_CATEGORY: activeSmartForm?.CATEGORY,
    };

    if (selectedCategory.length > 0) {
        return {
            CATEGORY_ID: selectedCategory[0]?.CATEGORY_ID,
            PURCHASING_CATEGORY: selectedCategory[0]?.CATEGORY,
            CATEGORY: selectedCategory[0]?.CATEGORY,
            CATEGORY_DISPLAY_NAME: selectedCategory[0]?.CATEGORY_DISPLAY_NAME,
        };
    }
    return defaultCategoryValues;
};

export const sortSmartformData = (smartformData, searchParams) => {
    if (smartformData.data) {
        smartformData.data.map((smartForm) => {
            if (!!smartForm.ITEM_DESCRIPTION == false)
                smartForm.ITEM_DESCRIPTION = smartForm.TEMPLATE_NAME;
        });
        if (searchParams.get("order_by") === "ITEM_DESCRIPTION")
            smartformData.data = smartformData.data.sort((a, b) =>
                a.ITEM_DESCRIPTION.localeCompare(b.ITEM_DESCRIPTION)
            );
        else if (searchParams.get("order_by") === "ITEM_DESCRIPTION desc")
            smartformData.data = smartformData.data.sort((a, b) =>
                b.ITEM_DESCRIPTION.localeCompare(a.ITEM_DESCRIPTION)
            );
    }
    return smartformData;
};

export const parseSmartFormUOMFromLookups = (activeSmartForm) => {
    const uomLookup =
        store.getState().filterPanelLookups.UNIT_OF_MEASURES || [];
    let unitMeasLookupCode = activeSmartForm?.UNIT_MEAS_LOOKUP_CODE;

    if (!unitMeasLookupCode) {
        const unitOfMeasure = activeSmartForm?.UNIT_OF_MEASURE;
        const unitOfMeasureObj = uomLookup.find(
            (uom) => uom.UNIT_OF_MEASURE === unitOfMeasure
        );

        if (unitOfMeasureObj) {
            activeSmartForm.UNIT_MEAS_LOOKUP_CODE =
                unitOfMeasureObj.UNIT_MEAS_LOOKUP_CODE;
        }
    }

    return {
        UNIT_OF_MEASURE: activeSmartForm?.UNIT_OF_MEASURE,
        UNIT_MEAS_LOOKUP_CODE: activeSmartForm?.UNIT_MEAS_LOOKUP_CODE,
    };
};
